import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PourCard } from '@/lib/apiClient';
import { todayInputValue, DATE_INPUT_MIN, DATE_INPUT_MAX } from '@/lib/dates';
import {
  type RangePreset, hasBounds, lookbackCutoff, lookbackWindow, includesDay,
  LOOKBACK_DAYS, openingDayIndex,
} from './pourRange';
import { fmtWallT, fmtDTm, chipStyle, bpoLabel, isRock, qtyLabel, ROCK_BROWN } from './pourShared';

// Concrete Pour Schedule — the PEs' Excel "Concrete Pour Schedule" rebuilt on
// live pour cards (their ask: "the schedule can be fed by the form cards as we
// mark them confirmed"). Jobs down the side, calendar days across the top
// (only days that have pours, like the sheet), each cell a pour chip:
// time / qty / mix, FILLED by ORDER STATE then POUR TYPE (the office workflow,
// 2026-07-20): a new/unconfirmed order is a white box with a black border;
// confirming fills it by type — will-call = yellow, main pour = orange, mud
// seal / flowable fill = grey, rock / aggregate delivery = brown; complete =
// green; cancelled = blue (struck).
// Deliberately grid-local — every other QA/QC surface keeps the app's
// standard status palette. Click a chip → the drawer.

/** Wall-clock day key (YYYY-MM-DD). pour_datetime is stored digit-for-digit
 *  as UTC, so the UTC date parts ARE the form's calendar day. */
function dayKey(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

function fmtDayHeader(key: string): { dow: string; md: string } {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return {
    dow: dt.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }),
    md: `${m}/${d}`,
  };
}

/** "12 pours" when the range shows everything, else "5 of 12 pours". */
function countLabel(shown: number, total: number, one: string, many: string): string {
  return shown === total ? `${total} ${total === 1 ? one : many}` : `${shown} of ${total} ${many}`;
}

function isWeekend(key: string): boolean {
  const [y, m, d] = key.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return dow === 0 || dow === 6;
}

const css = `
  /* ⚠️ The height bound is what makes the pinned header below WORK, so don't
     drop it. A position:sticky cell resolves against its nearest SCROLL
     CONTAINER, and this wrapper used to bound only the horizontal axis — its
     height was therefore always its content height, nothing ever scrolled
     inside it vertically, and the thead top:0 had nothing to pin against. The
     day columns scrolled away as soon as the job list got long, which on a grid
     whose whole point is "which day is this chip in" is the one heading you
     cannot afford to lose. Bounding the height puts the scroll inside the
     wrapper, where both axes can pin. The controls above and the legend below
     stay in view, which is the reason for a bound rather than a fixed height:
     a short list still takes only the room it needs. Same 72vh as the Manpower
     schedule grid and the Admin training table. */
  .psg-wrap { overflow: auto; max-height: 72vh; -webkit-overflow-scrolling: touch; }
  .psg { border-collapse: separate; border-spacing: 0; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); min-width: 100%; }
  .psg th, .psg td { border-bottom: 1px solid var(--ew-n-100); border-right: 1px solid var(--ew-n-100); vertical-align: top; }
  .psg thead th {
    padding: 8px 10px; text-align: center; white-space: nowrap; background: #fff;
    font: var(--ew-fw-semibold) 11px/1.3 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500);
    position: sticky; top: 0; z-index: 2;
  }
  .psg thead th.psg-wknd { background: var(--ew-n-50); }
  .psg thead th.psg-today { color: var(--ew-green-900); box-shadow: inset 0 -2px 0 var(--ew-green); }
  .psg .psg-job {
    position: sticky; left: 0; z-index: 1; background: #fff; text-align: left;
    padding: 10px 12px; width: 210px; min-width: 190px; max-width: 230px;
    border-right: 1px solid var(--ew-n-200);
  }
  .psg thead .psg-job { z-index: 3; }
  .psg td.psg-cell { padding: 4px; min-width: 108px; width: 130px; }
  /* Slack soak. The table is min-width:100% so it always spans the card, and an
     auto table layout hands leftover width to EVERY column — which on a week
     with two or three pour days blew the job column out to twice its content
     and stretched the chips with it (product owner, 2026-07-31: "the job column is too
     wide, too much blank space"). max-width doesn't bind in auto layout, so the
     fix is to give the surplus somewhere else to go: one borderless trailing
     column asking for 100% takes all of it, and the real columns keep the
     widths above. Collapses to nothing once the days fill the width. */
  .psg .psg-filler { width: 100%; padding: 0; border-right: none; background: #fff; }
  .psg thead th.psg-filler { background: #fff; }
  .psg td.psg-filler.psg-wknd { background: #fff; }
  .psg td.psg-wknd { background: var(--ew-n-50); }
  .psg-supplier {
    font: var(--ew-fw-semibold) 11px/1.3 var(--ew-font); color: var(--ew-quality-700);
    margin-top: 3px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .psg-bpo {
    font: var(--ew-fw-regular) 10.5px/1.3 var(--ew-font); color: var(--ew-n-500);
    margin-top: 1px; font-variant-numeric: tabular-nums;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .psg-nosetup { font: var(--ew-fw-regular) 10.5px/1.3 var(--ew-font); color: var(--ew-n-400); margin-top: 3px; }
  .psg-chip {
    display: block; width: 100%; text-align: left; border: 0; cursor: pointer;
    border-radius: var(--ew-r-sm); padding: 5px 7px; margin: 2px 0;
    font: var(--ew-fw-medium) 11.5px/1.35 var(--ew-font); white-space: nowrap;
  }
  .psg-chip:hover { filter: brightness(0.96); }
  /* Range controls. The two bare date inputs share one bordered shell so they
     read as a single field — the same treatment the Projects list uses for its
     start-date range, which PMs already know. */
  .psg-controls { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; padding: 10px 14px 0; }
  .psg-dates { display: inline-flex; align-items: center; gap: 4px; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 0 4px 0 10px; background: #fff; }
  .psg-dates .psg-lbl { font: var(--ew-fw-semibold) 12px/1 var(--ew-font); color: var(--ew-n-500); white-space: nowrap; }
  .psg-dates input { border: none; font: var(--ew-fw-regular) 13px var(--ew-font); color: var(--ew-dark); background: transparent; padding: 5px 2px; width: 128px; }
  .psg-dates .psg-jump { font: var(--ew-fw-semibold) 12px/1 var(--ew-font); padding: 5px 10px; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-sm); background: var(--ew-n-50); color: var(--ew-green-700); cursor: pointer; white-space: nowrap; }
  .psg-dates .psg-jump:hover { border-color: var(--ew-n-400); }
  .psg-dates .psg-x { font-size: 15px; line-height: 1; padding: 2px 6px; border: none; background: transparent; color: var(--ew-n-500); cursor: pointer; }
  .psg-count { margin-left: auto; font: var(--ew-fw-regular) 12px var(--ew-font); color: var(--ew-n-500); font-variant-numeric: tabular-nums; }
  /* On a phone the shell's natural ~455px is wider than the viewport, which
     makes the browser scale the WHOLE page down to fit rather than scrolling.
     Let it span the row and wrap internally instead. */
  @media (max-width: 560px) {
    .psg-dates { flex-wrap: wrap; width: 100%; }
    .psg-dates input { width: 118px; }
    .psg-count { margin-left: 0; }
  }
  .psg-legend { display: flex; flex-wrap: wrap; gap: 14px; padding: 10px 14px; border-top: 1px solid var(--ew-n-100); font: var(--ew-fw-regular) 11px var(--ew-font); color: var(--ew-n-500); }
  .psg-legend .sw { display: inline-block; width: 14px; height: 10px; border-radius: 3px; margin-right: 5px; vertical-align: -1px; }
`;

// Legend rows in workflow order (fills mirror chipStyle in pourShared).
const LEGEND: { bg: string; border: string; label: string }[] = [
  { bg: '#FFFFFF', border: '1px solid var(--ew-dark)', label: 'New — unconfirmed' },
  { bg: '#FFFF00', border: '1px solid var(--ew-n-300)', label: 'Confirmed will-call' },
  { bg: '#FFC000', border: '1px solid var(--ew-n-300)', label: 'Confirmed main pour' },
  { bg: '#BFBFBF', border: '1px solid var(--ew-n-300)', label: 'Confirmed mud seal / flowable fill' },
  { bg: ROCK_BROWN, border: '1px solid var(--ew-n-300)', label: 'Confirmed rock / aggregate' },
  { bg: '#92D050', border: '1px solid var(--ew-n-300)', label: 'Complete' },
  { bg: '#00B0F0', border: '1px solid var(--ew-n-300)', label: 'Cancelled' },
];

export default function PourScheduleGrid({ cards, onOpen }: {
  cards: PourCard[];
  onOpen: (id: number) => void;
}) {
  // "Upcoming" keeps the grid focused (their Excel moves old rows to an
  // Archive tab); "Everything" shows the full history. Neither could answer
  // "show me the next two months" (#327), so an explicit From/To sits alongside
  // them — see pourRange.ts for why bounds REPLACE the preset rather than
  // narrowing it.
  const [preset, setPreset] = useState<RangePreset>('upcoming');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const range = { preset, from, to };
  const custom = hasBounds(range);
  const choosePreset = (p: RangePreset) => { setPreset(p); setFrom(''); setTo(''); };

  const cutoff = useMemo(() => lookbackCutoff(new Date()), []);

  // Every dated, still-live pour — the pool the range filters, and the
  // denominator in the count below. 'covered' = a will-call confirmed with the
  // main pour → drops off the schedule (the concrete's accounted for under the
  // main pour).
  const live = useMemo(
    () => cards.filter((c) => c.pourDatetime != null && c.status !== 'covered'),
    [cards],
  );

  const dated = useMemo(
    () => live.filter((c) => includesDay({ preset, from, to }, cutoff, dayKey(c.pourDatetime!))),
    [live, preset, from, to, cutoff],
  );

  // Columns: only days that have pours, sorted (matches the sheet's compressed
  // calendar — a continuous range would be mostly empty cells).
  const days = useMemo(
    () => Array.from(new Set(dated.map((c) => dayKey(c.pourDatetime!)))).sort(),
    [dated],
  );

  // Rows: one per job (unmatched cards group under their raw job number).
  type Row = {
    key: string; jobNumber: string; title: string | null; projectId: number | null;
    supplier: string | null; bpo: string | null;
    /** Any concrete card in range. The job's supplier is its CONCRETE
     *  supplier, so a row of rock deliveries only doesn't show it. */
    concrete: boolean;
    byDay: Map<string, PourCard[]>;
  };
  const rows = useMemo(() => {
    const map = new Map<string, Row>();
    for (const c of dated) {
      const key = c.project ? `p${c.project.id}` : `raw:${c.jobNumberRaw ?? '?'}`;
      let row = map.get(key);
      if (!row) {
        // Supplier + blanket PO come down on the card (they're job-level, so
        // every card for a job carries the same values) — the PEs' ask was to
        // know who they're calling before opening anything.
        row = {
          key,
          jobNumber: c.project?.jobNumber ?? c.jobNumberRaw ?? '—',
          title: c.project?.title ?? null,
          projectId: c.project?.id ?? null,
          supplier: c.project?.concreteSupplier ?? null,
          bpo: bpoLabel(c.project),
          concrete: false,
          byDay: new Map(),
        };
        map.set(key, row);
      }
      if (!isRock(c)) row.concrete = true;
      const dk = dayKey(c.pourDatetime!);
      const list = row.byDay.get(dk) ?? [];
      list.push(c);
      row.byDay.set(dk, list);
    }
    // Chips within a cell sort by time-of-day.
    for (const r of map.values()) {
      for (const list of r.byDay.values()) {
        list.sort((a, b) => (a.pourDatetime! < b.pourDatetime! ? -1 : 1));
      }
    }
    return Array.from(map.values()).sort((a, b) => b.jobNumber.localeCompare(a.jobNumber, undefined, { numeric: true }));
  }, [dated]);

  // The VIEWER'S calendar date, which is what "today" means to someone reading the
  // grid. The UTC handling everywhere else in this file is deliberate — pour
  // datetimes are stored wall-clock digit-for-digit and read back as if UTC — but
  // that convention applies to STORED values, not to the current instant. Slicing
  // `new Date().toISOString()` here put the green underline on the next pour day
  // for every Eastern user after 8pm.
  const todayKey = todayInputValue();

  // Open scrolled to today: the first pour day on or after today sits flush
  // against the pinned Job column, so past days are one scroll to the left
  // rather than the first thing on screen (PE ask, 2026-09-24).
  //
  // Runs once per RANGE, not per render. The cards refetch after every office
  // action, and re-scrolling then would yank the grid away from whatever the
  // PE had scrolled to. Keyed on the range so picking a preset or dates snaps
  // back to today, which is what changing the range should look like.
  //
  // Layout effect so the first paint is already scrolled — no flash of the
  // oldest days first.
  const wrapRef = useRef<HTMLDivElement>(null);
  const jobHeadRef = useRef<HTMLTableCellElement>(null);
  const scrolledFor = useRef<string | null>(null);
  const rangeKey = `${preset}|${from}|${to}`;
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || days.length === 0 || scrolledFor.current === rangeKey) return;
    scrolledFor.current = rangeKey;
    const target = days[openingDayIndex(days, todayKey)];
    const th = wrap.querySelector<HTMLElement>(`th[data-day="${target}"]`);
    if (!th) return;
    // Rect deltas rather than offsetLeft: the header cells are position:sticky,
    // which makes offsetParent (and so offsetLeft) unreliable across browsers.
    const pinned = jobHeadRef.current?.getBoundingClientRect().width ?? 0;
    const delta = th.getBoundingClientRect().left - wrap.getBoundingClientRect().left - pinned;
    wrap.scrollLeft = Math.max(0, wrap.scrollLeft + delta);
  }, [days, rangeKey, todayKey]);


  return (
    <div>
      <style>{css}</style>
      <div className="psg-controls">
        <button className={`qr-pill${!custom && preset === 'upcoming' ? ' active' : ''}`} onClick={() => choosePreset('upcoming')}>Upcoming (last 7 days →)</button>
        <button className={`qr-pill${!custom && preset === 'all' ? ' active' : ''}`} onClick={() => choosePreset('all')}>Everything</button>

        <div className="psg-dates">
          <span className="psg-lbl">Pour dates</span>
          <input
            type="date" aria-label="Pour date from"
            min={DATE_INPUT_MIN} max={to || DATE_INPUT_MAX}
            value={from} onChange={(e) => setFrom(e.target.value)}
          />
          <span style={{ color: 'var(--ew-n-400)' }}>–</span>
          <input
            type="date" aria-label="Pour date to"
            min={from || DATE_INPUT_MIN} max={DATE_INPUT_MAX}
            value={to} onChange={(e) => setTo(e.target.value)}
          />
          {/* One click for the long view the range filter was asked for.
              BACKWARD, because that is the only direction this grid has depth
              in — see lookbackWindow. */}
          <button
            type="button" className="psg-jump"
            title={`Show the last ${LOOKBACK_DAYS} days of pours, plus everything still upcoming`}
            onClick={() => { const w = lookbackWindow(new Date()); setFrom(w.from); setTo(w.to); }}
          >
            Last {LOOKBACK_DAYS} days →
          </button>
          {custom && (
            <button
              type="button" className="psg-x"
              title="Clear the date range" aria-label="Clear the date range"
              onClick={() => { setFrom(''); setTo(''); }}
            >
              ×
            </button>
          )}
        </div>

        {/* Widening the range should visibly do something even when the extra
            pours are off the right-hand edge of a scrolled grid. */}
        <span className="psg-count">
          {/* Pours and rock deliveries are counted apart (a delivery of stone
              isn't a pour), each only when there are any, so a rock-only list
              doesn't open with "0 pours". */}
          {[
            (!live.some(isRock) || live.some((c) => !isRock(c)))
              && countLabel(dated.filter((c) => !isRock(c)).length, live.filter((c) => !isRock(c)).length, 'pour', 'pours'),
            live.some(isRock)
              && countLabel(dated.filter(isRock).length, live.filter(isRock).length, 'rock delivery', 'rock deliveries'),
          ].filter(Boolean).join(' · ')}
          {days.length > 0 && ` · ${days.length} day${days.length === 1 ? '' : 's'}`}
        </span>
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: 'var(--ew-s-5)', color: 'var(--ew-n-500)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
          No scheduled pours in this range.
        </div>
      ) : (
        <div className="psg-wrap" style={{ marginTop: 10 }} ref={wrapRef}>
          <table className="psg">
            <thead>
              <tr>
                <th className="psg-job" ref={jobHeadRef}>Job</th>
                {days.map((d) => {
                  const h = fmtDayHeader(d);
                  return (
                    <th key={d} data-day={d} className={`${isWeekend(d) ? 'psg-wknd' : ''}${d === todayKey ? ' psg-today' : ''}`}>
                      <div>{h.dow}</div>
                      <div style={{ fontSize: 12, marginTop: 1, color: 'var(--ew-dark)' }}>{h.md}</div>
                    </th>
                  );
                })}
                <th className="psg-filler" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td className="psg-job">
                    {r.projectId ? (
                      <Link to={`/projects/${r.projectId}?tab=qaqc`} style={{ color: 'var(--ew-green-700)', fontWeight: 600, textDecoration: 'none' }}>
                        {r.jobNumber}
                      </Link>
                    ) : (
                      <span style={{ color: 'var(--ew-safety-700)', fontWeight: 600 }} title="No matching job — open a chip to assign">{r.jobNumber}</span>
                    )}
                    {r.title && <div style={{ font: 'var(--ew-fw-regular) 11px/1.3 var(--ew-font)', color: 'var(--ew-n-500)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</div>}
                    {/* Supplier & blanket PO — the two columns of the office's
                        sheet the tracker never carried. Supplier leads: it's
                        who you dial, and the PE ask called it "key to
                        streamlining the process". */}
                    {r.concrete && r.supplier && <div className="psg-supplier" title={`Concrete supplier: ${r.supplier}`}>{r.supplier}</div>}
                    {r.bpo && <div className="psg-bpo" title="Blanket PO on this job">{r.bpo}</div>}
                    {r.projectId && r.concrete && !r.supplier && !r.bpo && (
                      <div className="psg-nosetup" title="Recorded under Concrete Ordering on the job's QA/QC tab — the estimator raises the blanket PO; the supplier is theirs or the PM's depending on the job">
                        No supplier / BPO set
                      </div>
                    )}
                  </td>
                  {days.map((d) => {
                    const list = r.byDay.get(d);
                    return (
                      <td key={d} className={`psg-cell${isWeekend(d) ? ' psg-wknd' : ''}`}>
                        {list?.map((c) => {
                          const sc = chipStyle(c.status, c.pourType);
                          return (
                            <button
                              key={c.id}
                              className="psg-chip"
                              style={{ background: sc.bg, border: sc.border, color: sc.fg ?? 'var(--ew-dark)', textDecoration: sc.strike ? 'line-through' : undefined }}
                              onClick={() => onOpen(c.id)}
                              title={`${isRock(c) ? `Rock / Aggregate${c.element ? `: ${c.element}` : ''}` : (c.element ?? 'Pour')}${c.vendor ? ` · ${c.vendor}` : ''} — submitted ${fmtDTm(c.createdAt)}`}
                            >
                              <span style={{ fontWeight: 600 }}>{fmtWallT(c.pourDatetime)}</span>
                              {qtyLabel(c) ? ` · ${qtyLabel(c)}` : ''}
                              {/* Rock's "what" is the aggregate type, which lives in element. */}
                              {isRock(c) ? (c.element ? ` · ${c.element}` : '') : (c.mix ? ` · ${c.mix}` : '')}
                            </button>
                          );
                        })}
                      </td>
                    );
                  })}
                  <td className="psg-filler" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="psg-legend">
        {LEGEND.map((s) => (
          <span key={s.label}><span className="sw" style={{ background: s.bg, border: s.border }} />{s.label}</span>
        ))}
        <span>Hover a pour for element, vendor & submitted time · click for the full card</span>
      </div>
    </div>
  );
}
